import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  styled,
  Skeleton,
  Drawer,
  IconButton,
  useMediaQuery,
  InputAdornment,
  TextField,
  Dialog,
} from "@mui/material";
import { CircularProgress } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RefreshIcon from "@mui/icons-material/Refresh";
import { COLORS, fontFamilyAll } from "../../assets/commonComponents/COLORS";
import { KnowledgeBaseContext } from "../../components/knowledgeBaseContext/KnowledgeBaseContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// Sidebar styles (unchanged)
const SidebarContainer = styled(Box)(({ theme }) => ({
  width: "250px",
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
  height: "100%",
  position: "relative",
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: COLORS.secondaryColor,
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": { background: "#6f5b8a" },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  overflowY: "auto",
  backgroundColor:COLORS.primaryColor,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  width: "90%",
  "& > div": {
    width: "100%",
    maxWidth: "900px",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(3),
    margin: "0 auto",
  },
  "&::-webkit-scrollbar": { width: "10px" },
  "&::-webkit-scrollbar-track": {
    background: COLORS.lightgreenColor,
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: COLORS.secondaryColor,
    borderRadius: "10px",
  },
  "&::-webkit-scrollbar-thumb:hover": { background: COLORS.secondaryColor },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  boxShadow: "none",
  marginBottom: theme.spacing(1),
  "&:before": { display: "none" },
  "&.Mui-expanded": { margin: "auto" },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: COLORS.lightgreenColor,
  minHeight: 48,
  "& .MuiAccordionSummary-content": { margin: 0 },
  "&.Mui-expanded": { minHeight: 48 },
  "& .MuiSvgIcon-root": { transition: "transform 0.3s ease" },
  "&.Mui-expanded .MuiSvgIcon-root": { transform: "rotate(180deg)" },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: "white",
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  "&.Mui-selected": {
    backgroundColor: COLORS.secondaryColor,
    color: "white",
    fontWeight: 600,
    "&:hover": { color: "white", backgroundColor: COLORS.secondaryColor },
  },
}));

// Highlight text (unchanged)
const highlightText = (text, term) => {
  if (!term) return text;
  const regex = new RegExp(`(${term})`, "gi");
  const parts = text.split(regex);
  return parts.map((part, index) =>
    regex.test(part) ? (
      <span
        key={index}
        style={{
          backgroundColor: COLORS.lightgreenColor,
        }}
      >
        {part}
      </span>
    ) : (
      part
    )
  );
};

// Function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes("youtube.com")) {
      return urlObj.searchParams.get("v");
    } else if (urlObj.hostname.includes("youtu.be")) {
      return urlObj.pathname.slice(1);
    }
    return null;
  } catch {
    return null;
  }
};

function SidebarWithContent() {
  const {
    articles,
    setSelectedRecordid,
    individualArticle,
    categoryName,
    searchTerm,
    mobileOpenSideBar,
    setMobileOpensideBar,
    setSearchTerm,
  } = useContext(KnowledgeBaseContext);

  const { recordid } = useParams();
  const article = articles.find((item) => item.recordid === recordid);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expanded, setExpanded] = useState([article?.name]);
  const [loading, setLoading] = useState(true);
  const [openImage, setOpenImage] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const location = useLocation();
  const isSmall = useMediaQuery("(max-width: 600px)");
  const isMobile = useMediaQuery("(max-width:768px)");
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 800);
    return () => clearTimeout(timer);
  }, [articles, searchTerm]);

  useEffect(() => {
    if (article?.recordid) {
      setSelectedRecordid(article.recordid);
    }
  }, [article]);

  useEffect(() => {
    if (individualArticle?.recordid) {
      setSelectedItem({
        recordid: individualArticle.recordid,
        content: individualArticle.content,
        videourl: individualArticle.videourl || "#",
        label: individualArticle.title || individualArticle.name,
      });
      setExpanded([
        individualArticle?.knowledgebasecategoryknowledgebasearticleid?.name,
      ]);
    }
  }, [individualArticle]);

  useEffect(() => {
    if (loading || !sidebarRef.current || expanded.length === 0) return;
    const activeCategory = expanded[0];
    const accordionElement = sidebarRef.current.querySelector(
      `[id="${activeCategory}-header"]`
    );
    if (accordionElement) {
      const sidebarRect = sidebarRef.current.getBoundingClientRect();
      const accordionRect = accordionElement.getBoundingClientRect();
      const scrollTop =
        accordionRect.top - sidebarRect.top + sidebarRef.current.scrollTop;
      sidebarRef.current.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  }, [loading]);

  const handleAccordionChange = useCallback(
    (panel) => (event, isExpanded) => {
      setExpanded((prev) =>
        isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
      );
    },
    []
  );

  const sidebarConfig = useMemo(() => {
    const term = searchTerm?.toLowerCase().trim() || "";
    const filteredArticles = articles.filter((article) => {
      const name = article.name?.toLowerCase() || "";
      const content =
        article.knowledgebasecategoryknowledgebasearticleid.name?.toLowerCase() ||
        "";
      return (
        article.knowledgebasecategoryknowledgebasearticleid?.isactive === true &&
        (term === "" || name.includes(term) || content.includes(term))
      );
    });

    const grouped = Array.from(
      new Set(
        filteredArticles.map(
          (a) => a.knowledgebasecategoryknowledgebasearticleid.name
        )
      )
    ).map((category) => ({
      title: category,
      items: filteredArticles
        .filter(
          (a) => a.knowledgebasecategoryknowledgebasearticleid.name === category
        )
        .map((a) => ({
          label: a.name,
          recordid: a.recordid,
          content: a.content,
          videourl: a.videourl || "#",
        })),
    }));

    if (term && grouped.length > 0) {
      setExpanded(grouped.map((g) => g.title));
    }

    return grouped;
  }, [articles, searchTerm]);

  const hasYouTubeLink = useMemo(() => {
    if (!selectedItem?.content) return false;
    return selectedItem.content.includes('href="https://www.youtube.com');
  }, [selectedItem]);

  const renderedContent = useMemo(() => {
    if (loading) {
      return <Skeleton variant="rectangular" height={400} animation="wave" />;
    }
    // if (!selectedItem) {
    //   return (
    //     <Typography
    //       variant="h6"
    //       sx={{
    //         fontFamily: fontFamilyAll,
    //         color: COLORS.secondaryColor,
    //         textAlign: "center",
    //       }}
    //     >
    //       Please select an article to view its content.
    //     </Typography>
    //   );
    // }

    const cleanedCSS = selectedItem?.content
      ?.replace(/body\s*\{[^}]*\}/gi, "")
      ?.replace(/a:hover\s*\{[^}]*\}/gi, "");

    return (
      <div
        dangerouslySetInnerHTML={{ __html: cleanedCSS }}
        onClick={(e) => {
          if (e.target.tagName === "IMG") {
            setOpenImage(e.target.src);
            setImageOpen(true);
          }
        }}
      />
    );
  }, [selectedItem, loading]);

  const sidebarUI = (
    <SidebarContainer ref={sidebarRef}>
      {loading ? (
        Array.from({ length: 4 }).map((_, idx) => (
          <Skeleton
            key={idx}
            variant="rectangular"
            height={50}
            sx={{ mb: 1, borderRadius: 2 }}
          />
        ))
      ) : sidebarConfig.length === 0 && searchTerm ? (
        <Typography
          variant="body1"
          sx={{
            fontFamily: fontFamilyAll,
            color: "#555",
            textAlign: "center",
            mt: 2,
          }}
        >
          No results found.
        </Typography>
      ) : (
        sidebarConfig.map((section, index) => (
          <StyledAccordion
            key={section.title}
            expanded={expanded.includes(section.title)}
            onChange={handleAccordionChange(section.title)}
            TransitionProps={{ unmountOnExit: true }}
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${section.title}-content`}
              id={`${section.title}-header`}
            >
              <Typography
                variant="subtitle1"
                fontWeight="600"
                fontFamily={fontFamilyAll}
                color={COLORS.secondaryColor}
              >
                {highlightText(section.title, searchTerm)}
              </Typography>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <List component="nav" aria-label={`Articles in ${section.title}`}>
                {section.items.map((item) => (
                  <StyledListItemButton
                    key={item.recordid}
                    selected={selectedItem?.recordid === item.recordid}
                  onClick={() => {
    if (selectedItem?.recordid !== item.recordid) {
      setSelectedRecordid(item.recordid);
      setSelectedItem(item);
      navigate(`/knowledgebase/document/${item.recordid}`);
      if (isMobile) setMobileOpensideBar(false);
    }
  }}
  onDoubleClick={(e) => e.preventDefault()} //
                    aria-label={item.label}
                  >
                    <ListItemText
                      primary={highlightText(item.label, searchTerm)}
                      primaryTypographyProps={{
                        fontFamily: fontFamilyAll,
                        fontSize: "14px",
                      }}
                    />
                  </StyledListItemButton>
                ))}
              </List>
            </StyledAccordionDetails>
            {index < sidebarConfig.length - 1 && <Divider />}
          </StyledAccordion>
        ))
      )}
      <br />
      <br />
    </SidebarContainer>
  );

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  // Get YouTube video ID
  const videoId = selectedItem?.videourl ? getYouTubeVideoId(selectedItem.videourl) : null;

  return (
    <Box
      display="flex"
      sx={{
        flexDirection: { xs: "column", sm: "row" },
        mt: "120px",
        height: "calc(100vh - 140px)",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        <Drawer
          anchor="left"
          open={mobileOpenSideBar}
          onClose={() => setMobileOpensideBar(false)}
          PaperProps={{
            sx: { width: "80%", maxWidth: "300px" },
          }}
        >
          <Box
            display="flex"
            justifyContent="flex-end"
            p={1}
            sx={{ borderBottom: "1px solid #ddd", backgroundColor: COLORS.secondaryColor }}
          >
            <TextField
              placeholder="Search here..."
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CancelIcon />
                  </IconButton>
                ),
              }}
              sx={{
                width: location.pathname.includes("/document")
                  ? isSmall
                    ? "100%"
                    : "220px"
                  : isSmall
                  ? "100%"
                  : "700px",
                bgcolor: "#fff",
                borderRadius: "50px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "50px",
                  fontFamily: fontFamilyAll,
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: COLORS.lightgreenColor },
                  "&.Mui-focused fieldset": { borderColor: COLORS.lightgreenColor },
                },
                "& input": {
                  padding: "6px 20px",
                },
              }}
            />
            <IconButton
              onClick={() => setMobileOpensideBar(false)}
              aria-label="Close sidebar"
            >
              <CloseIcon sx={{ color: "white" }} size="large" />
            </IconButton>
          </Box>
          {sidebarUI}
        </Drawer>
      ) : (
        sidebarUI
      )}

      <ContentContainer>
        <div>
          {renderedContent}
          {!loading && selectedItem && (
            <Box sx={{ mt: 2, textAlign: "left" }}>
              {videoId && !hasYouTubeLink ? (
                <>
                  <Typography
                    component="span"
                    textAlign="left"
                    sx={{
                      fontFamily: fontFamilyAll,
                      color: "#555",
                      marginLeft: "20px",
                      fontSize: "16px",
                    }}
                  >
                    Learn more by watching the full video on YouTube:{" "}
                  </Typography>
                    <Typography
                    component="a"
                    textAlign="left"
                    href={selectedItem.videourl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      fontFamily: fontFamilyAll,
                      color: COLORS.secondaryColor,
                      textDecoration: "underline",
                      fontSize: "16px",
                    }}
                  >
                    {selectedItem.videourl}
                  </Typography>
                  <br/>
                  <br/>
            <Box sx={{ position: "relative", paddingTop: "56.25%" /* 16:9 aspect ratio */ }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    title={selectedItem.label}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: "8px",
                    }}
                  />
                </Box>
          
                
              </>
              ) :  (
                <>
                <Skeleton variant="rectangular" height={400} animation="wave" />
                </>
              )}
              <br />
              <br />
            </Box>
          )}
        </div>
      </ContentContainer>

      <Dialog
        open={imageOpen}
        onClose={() => {
          setImageOpen(false);
          setZoomLevel(1);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            position: "relative",
            bgcolor: "#fafafa",
            boxShadow: 6,
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => {
            setImageOpen(false);
            setZoomLevel(1);
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: COLORS.secondaryColor,
            "&:hover": {
              bgcolor: "rgba(230,230,230,0.9)",
            },
            zIndex: 2,
          }}
        >
          <CloseIcon sx={{ fontSize: 18, fontWeight: 600 }} />
        </IconButton>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
            bgcolor: "#fff",
          }}
        >
          {imageLoading && (
            <CircularProgress
              size={40}
              sx={{
                color: COLORS.secondaryColor,
                position: "absolute",
              }}
            />
          )}
          <img
            src={openImage}
            alt="Preview"
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
            style={{
              transform: `scale(${zoomLevel})`,
              transition: "transform 0.2s ease-in-out",
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              display: imageLoading ? "none" : "block",
            }}
          />
          <Box
            sx={{
              mt: 2,
              display: "flex",
              gap: 2,
              justifyContent: "center",
            }}
          >
            <IconButton
              onClick={() => setZoomLevel((prev) => Math.max(0.5, prev - 0.2))}
              aria-label="Zoom Out"
              sx={{ bgcolor: COLORS.secondaryColor, color: "white", "&:hover": { bgcolor: COLORS.secondaryColor } }}
            >
              <RemoveIcon />
            </IconButton>
            <IconButton
              onClick={() => setZoomLevel(1)}
              aria-label="Reset Zoom"
              sx={{ bgcolor: COLORS.secondaryColor, color: "white", "&:hover": { bgcolor: COLORS.secondaryColor } }}
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              onClick={() => setZoomLevel((prev) => Math.min(3, prev + 0.2))}
              aria-label="Zoom In"
              sx={{ bgcolor: COLORS.secondaryColor, color: "white", "&:hover": { bgcolor: COLORS.secondaryColor } }}
            >
              <AddIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default SidebarWithContent;